import { localized, t } from '@weavedev/lit-i18next';
import { css, html, LitElement, nothing, PropertyValues } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';

import { iconCheckmark, iconPencil, iconTrashCan } from '@/components/icons';
import { PersonalisationType } from '@/context';
import { baseStyles, buttonStyles, typographyStyles } from '@/styles';

@customElement('fg-personalisation-status-bar')
@localized()
export class FGPersonalisationStatusBar extends LitElement {
  @property({ type: Object })
  personalisationData?: PersonalisationType;

  @property({ type: String })
  printOptionName?: string;

  @property({ type: Boolean })
  showActions = true;

  @state()
  hasPersonalisation = false;

  openPersonalisationModal = () => {
    this.dispatchEvent(new CustomEvent('open'));
  };

  editPersonalisation = () => {
    if (!this.personalisationData?.visualDesignId) {
      throw new Error('Visual design ID is required');
    }

    this.dispatchEvent(
      new CustomEvent('open', {
        composed: true,
        detail: {
          option: {
            visualDesignId: this.personalisationData.visualDesignId,
          },
        },
      }),
    );
  };

  removePersonalisation = () => {
    this.dispatchEvent(new CustomEvent('remove'));
  };

  protected willUpdate(_changedProperties: PropertyValues) {
    if (_changedProperties.has('personalisationData')) {
      const { logoUuid, textPersonalization, visualDesignId } =
        this.personalisationData || {};
      if (logoUuid || textPersonalization || visualDesignId) {
        this.hasPersonalisation = true;
        return;
      }
      this.hasPersonalisation = false;
    }
  }

  handlePersonalisationData = (personalisationData?: PersonalisationType) => {
    if (!this.hasPersonalisation || !personalisationData) {
      return html`
        <span>${t('labels.logoOrTextNotUploaded')}</span>
      `;
    }
    const { textPersonalization, uploadedFileName, logoUuid, visualDesignId } =
      personalisationData;

    return html`
      <span>
        ${iconCheckmark}
        ${uploadedFileName && logoUuid ? uploadedFileName : nothing}
        ${uploadedFileName && textPersonalization ? '-' : nothing}
        ${textPersonalization ? `"${textPersonalization.text}"` : nothing}
        ${visualDesignId
          ? t('labels.personalisationMadeInVisualDesigner')
          : nothing}
      </span>
    `;
  };

  handleEditButton = (personalisationData?: PersonalisationType) => {
    if (personalisationData?.visualDesignId) {
      return html`
        <button class="edit" @click=${this.editPersonalisation}>
          ${t('actions.edit')}
          <span class="icon-wrapper" aria-hidden="true">${iconPencil}</span>
        </button>
      `;
    }
    return nothing;
  };

  render() {
    return html`
      <div
        class="personalisation-status-bar ${classMap({
          active: this.hasPersonalisation,
        })}"
      >
        ${this.handlePersonalisationData(this.personalisationData)}
        ${this.showActions
          ? !this.hasPersonalisation
            ? html`
                <button
                  class="button button-secondary"
                  @click=${this.openPersonalisationModal}
                >
                  ${t('actions.uploadLogoText')}
                </button>
              `
            : html`
                <span class="buttons-wrapper">
                  ${this.handleEditButton(this.personalisationData)}
                  <button class="remove" @click=${this.removePersonalisation}>
                    <span class="icon-wrapper" aria-hidden="true">
                      ${iconTrashCan}
                    </span>
                    <span class="sr-only">
                      ${t('labels.removePrintOptionPersonalisation', {
                        name: this.printOptionName,
                      })}
                    </span>
                  </button>
                </span>
              `
          : nothing}
      </div>
    `;
  }

  static styles = [
    baseStyles,
    typographyStyles,
    buttonStyles,
    css`
      .personalisation-status-bar {
        display: flex;
        justify-content: space-between;
        align-items: center;
        min-height: 64px;
        background-color: #ffffff;
        padding: 0.75rem;
        border: 1px solid var(--fg-gray--300, var(--fg-fallback-gray--300));
        gap: 0.5rem;
        border-radius: var(
          ---fg---fg-borderradius--xs,
          var(--fg-fallback-borderradius--xs)
        );

        &.active {
          border: 1px solid var(--fg-success, var(--fg-fallback-success));

          & > span {
            color: var(--fg-success--dark, var(--fg-fallback-success-dark));
          }
        }

        & > span {
          font-style: italic;
        }
      }

      .buttons-wrapper {
        display: flex;
        gap: 0.5rem;
        align-items: end;
      }

      .edit {
        align-items: center;
        color: var(--fg-secondary, var(--fg-fallback-secondary));
        display: flex;
        font-style: normal;
        gap: 0.5rem;
        span.icon-wrapper {
          display: grid;
          background-color: var(--fg-secondary, var(--fg-fallback-secondary));
          border-radius: 999px;
          padding: 5px;
          svg {
            width: 12px;
            height: 12px;
            color: #ffffff;
          }
        }
      }

      .remove {
        span.icon-wrapper {
          display: grid;
          border: 1px solid var(--fg-gray--300, var(--fg-fallback-gray--300));
          border-radius: 999px;
          padding: 5px;
          svg {
            width: 12px;
            height: 12px;
            color: var(--fg-red--500, var(--fg-fallback-red--500));
          }
        }
      }
    `,
  ];
}
